import { useMemo } from 'react';
import { useI18n } from 'next-localization';
import Checkbox from 'components/Generic/Checkbox';
import Loader from 'components/Generic/Loader';
import styles from '../styles/FilterDropdownOption.module.scss';
import { FilterOption } from 'hooks/useFilters';

type Props = {
  filterOption: FilterOption;
  isFetching: boolean;
  isVisible: boolean;
  onClick: () => void;
  onKeyDown: (option: FilterOption) => void;
  selectFilterOptionWithArrow: (direction: string) => void;
};

const FilterDropdownOption = ({
  filterOption,
  isVisible,
  isFetching,
  onClick,
  selectFilterOptionWithArrow,
  onKeyDown,
}: Props) => {
  const { t } = useI18n();
  const isDisabledOption = useMemo(() => {
    return (
      ((filterOption.showAggregateCount ? !filterOption.aggregateCount : false) &&
        !filterOption.selected) ||
      isFetching
    );
  }, [filterOption, isFetching]);

  const isFocusable = useMemo(() => {
    return isVisible && !isDisabledOption;
  }, [isDisabledOption, isVisible]);

  const fundsDictionaryItem = t('allFunds').split(' ')[1];

  return (
    <div
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.code === 'Tab') return;
        e.preventDefault();
        if (e.code === 'ArrowUp') {
          selectFilterOptionWithArrow('up');
        } else if (e.code === 'ArrowDown') {
          selectFilterOptionWithArrow('down');
        } else if (e.code === 'Space' || e.code === 'Enter') {
          onKeyDown(filterOption);
        }
      }}
      role="button"
      tabIndex={isFocusable ? '0' : '-1'}
      className={`d-flex ${styles['filter-dropdown-option']} ${
        isDisabledOption ? styles.disabled : ''
      }`}
    >
      <Checkbox
        className={`pe-none ${styles['checkbox-wrapper']}`}
        tabIndex={-1}
        selected={filterOption.selected}
        label={filterOption.text}
      />
      {filterOption.showAggregateCount && (
        <div className="d-flex pe-none loader-wrapper">
          {isFetching ? (
            <Loader loading={isFetching} />
          ) : (
            <p className="ms-auto">
              {filterOption.aggregateCount}
              <span className="sr-only">
                {fundsDictionaryItem}.
                {filterOption.selected ? t('checkboxChecked') : t('checkboxNotChecked')}.
              </span>
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default FilterDropdownOption;
